import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from "axios"
import toast from 'react-hot-toast'
import { PROCESS_TRACKING } from '../../../../../../utility/SourceAPI'

export const getAllProcessTracking = createAsyncThunk('appProcessTracking/getAllProcessTracking', async () => {
    try {
        // Configurar headers más pequeños para esta petición específica
        const config = {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            // Reducir timeout para evitar acumulación de headers
            timeout: 30000
        }

        const response = await axios.get(PROCESS_TRACKING.concat('group_by_company'), config)

        // Optimizar datos para reducir duplicación y mejorar renderizado
        const optimizedData = response.data.map((item, index) => {
            // Crear una referencia única para cada item
            const company = item.company_preview || item.company || {}

            return {
                id: item.id || index, // Asegurar que hay un ID único
                company_preview: {
                    id: company.id,
                    rfc: company.rfc || '',
                    alias: company.alias || '',
                    name: company.name || '',
                    email: company.email || '',
                    phone: company.phone || '',
                    contact: company.contact || '',
                    social_address: company.social_address || '',
                    // Solo mantener campos esenciales para la tabla
                    status_company: company.status_company,
                    legal_representative: company.legal_representative ? {
                        full_name: company.legal_representative.full_name,
                        id: company.legal_representative.id
                    } : null,
                    partner: company.partner ? {
                        full_name: company.partner.full_name,
                        id: company.partner.id
                    } : null
                },
                // Mantener referencias a los tracking pero optimizadas
                at_rfc_registration: item.at_rfc_registration ? {
                    start_date: item.at_rfc_registration.start_date,
                    assigned_to: item.at_rfc_registration.assigned_to,
                    delegation: item.at_rfc_registration.delegation,
                    appointment_number: item.at_rfc_registration.appointment_number,
                    appointment_date: item.at_rfc_registration.appointment_date,
                    appointment_time: item.at_rfc_registration.appointment_time,
                    observations: item.at_rfc_registration.observations,
                    id: item.at_rfc_registration.id,
                    status: item.at_rfc_registration.status,
                    registrated_at: item.at_rfc_registration.registrated_at,
                    updated_at: item.at_rfc_registration.updated_at,
                    registrated_by: item.at_rfc_registration.registrated_by,
                    updated_by: item.at_rfc_registration.updated_by,
                    partner: item.at_rfc_registration.partner,
                    conclusion_date: item.at_rfc_registration.conclusion_date
                } : null,
                at_e_signature: item.at_e_signature ? {
                    start_date: item.at_e_signature.start_date,
                    conclusion_date: item.at_e_signature.conclusion_date,
                    assigned_to: item.at_e_signature.assigned_to,
                    delegation: item.at_e_signature.delegation,
                    appointment_number: item.at_e_signature.appointment_number,
                    appointment_date: item.at_e_signature.appointment_date,
                    appointment_time: item.at_e_signature.appointment_time,
                    observations: item.at_e_signature.observations,
                    id: item.at_e_signature.id,
                    status: item.at_e_signature.status,
                    registrated_at: item.at_e_signature.registrated_at,
                    updated_at: item.at_e_signature.updated_at,
                    registrated_by: item.at_e_signature.registrated_by,
                    updated_by: item.at_e_signature.updated_by,
                    partner: item.at_e_signature.partner
                } : null,
                at_bank_account: item.at_bank_account ? {
                    id: item.at_bank_account.id,
                    status: item.at_bank_account.status,
                    account_opening_date: item.at_bank_account.account_opening_date,
                    ...item.at_bank_account // Incluir otros campos si es necesario
                } : null,
                // Datos originales completos guardados por si se necesitan
                _original: item
            }
        })

        return {
            data: optimizedData,
            totalCount: optimizedData.length
        }
    } catch (error) {
        toast.error('Error al cargar el seguimiento de procesos')
        throw error
    }
})

export const createProcessTracking = createAsyncThunk('appProcessTracking/createProcessTracking', async (processData, { dispatch }) => {
    try {
        const response = await axios.post(PROCESS_TRACKING, processData)
        await dispatch(getAllProcessTracking()) // Refrescar datos después de la creación
        return {
            data: response.data
        }
    } catch (error) {
        toast.error('Error al crear el proceso de seguimiento')
        throw error
    }
})

export const updateProcessTrackingRFC = createAsyncThunk('appProcessTracking/updateProcessTrackingRFC', async ({ rfcData }, { dispatch }) => {
    try {
        const response = await axios.put(`${PROCESS_TRACKING}${rfcData.id}`, rfcData)
        await dispatch(getAllProcessTracking()) // Refrescar datos después de la actualización
        return {
            data: response.data
        }
    } catch (error) {
        toast.error('Error al actualizar el registro RFC')
        throw error
    }
})

export const updateProcessTrackingEfirma = createAsyncThunk('appProcessTracking/updateProcessTrackingEfirma', async ({ efirmaData }, { dispatch }) => {
    try {
        // Asumiendo que el endpoint para e-firma será diferente, ajustar según sea necesario
        const response = await axios.put(`${PROCESS_TRACKING.replace('at_rfc_registration', 'at_e_signature')}${efirmaData.id}`, efirmaData)
        await dispatch(getAllProcessTracking()) // Refrescar datos después de la actualización
        return {
            data: response.data
        }
    } catch (error) {
        toast.error('Error al actualizar el registro de e-firma')
        throw error
    }
})

export const updateProcessTrackingBankAccount = createAsyncThunk('appProcessTracking/updateProcessTrackingBankAccount', async ({ bankAccountId, bankAccountData }, { dispatch }) => {
    try {        
        const response = await axios.put(`${PROCESS_TRACKING.replace('at_rfc_registration', 'at_bank_account')}${bankAccountId}`, bankAccountData)
        await dispatch(getAllProcessTracking()) // Refrescar datos después de la actualización
        return {
            data: response.data
        }
    } catch (error) {
        toast.error('Error al actualizar el proceso de cuenta bancaria')
        throw error
    }
})

export const processTrackingSlice = createSlice({
    name: 'appProcessTracking',
    initialState: {
        data: [],
        loading: false,
        error: null,
        totalCount: 0
    },
    reducers: {
        clearProcessTracking: (state) => {
            state.data = []
            state.error = null
            state.totalCount = 0
        }
    },
    extraReducers: builder => {
        builder
            .addCase(getAllProcessTracking.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(getAllProcessTracking.fulfilled, (state, action) => {
                state.loading = false
                state.data = action.payload.data
                state.totalCount = action.payload.totalCount || 0
                state.error = null
            })
            .addCase(getAllProcessTracking.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
                toast.error('Error al cargar el seguimiento de procesos')
            })
            .addCase(createProcessTracking.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(createProcessTracking.fulfilled, (state) => {
                state.loading = false
                state.error = null
                toast.success('Proceso creado exitosamente')
            })
            .addCase(createProcessTracking.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
                toast.error('Error al crear el proceso')
            })
            .addCase(updateProcessTrackingRFC.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(updateProcessTrackingRFC.fulfilled, (state, action) => {
                state.loading = false
                state.error = null
                // Actualizar el registro específico en el array de datos
                const updatedRfc = action.payload.data
                const index = state.data.findIndex(item => item.at_rfc_registration?.id === updatedRfc.id)
                if (index !== -1) {
                    state.data[index].at_rfc_registration = {
                        ...state.data[index].at_rfc_registration,
                        ...updatedRfc
                    }
                }
                toast.success('Registro RFC actualizado exitosamente')
            })
            .addCase(updateProcessTrackingRFC.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
                toast.error('Error al actualizar el registro RFC')
            })
            .addCase(updateProcessTrackingEfirma.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(updateProcessTrackingEfirma.fulfilled, (state, action) => {
                state.loading = false
                state.error = null
                // Actualizar el registro específico en el array de datos
                const updatedEfirma = action.payload.data
                const index = state.data.findIndex(item => item.at_e_signature?.id === updatedEfirma.id)
                if (index !== -1) {
                    state.data[index].at_e_signature = {
                        ...state.data[index].at_e_signature,
                        ...updatedEfirma
                    }
                }
                toast.success('Registro de e-firma actualizado exitosamente')
            })
            .addCase(updateProcessTrackingEfirma.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
                toast.error('Error al actualizar el registro de e-firma')
            })
            .addCase(updateProcessTrackingBankAccount.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(updateProcessTrackingBankAccount.fulfilled, (state, action) => {
                state.loading = false
                state.error = null
                // Actualizar el registro específico en el array de datos
                const updatedBankAccount = action.payload.data
                const index = state.data.findIndex(item => item.at_bank_account?.id === updatedBankAccount.id)
                if (index !== -1) {
                    state.data[index].at_bank_account = {
                        ...state.data[index].at_bank_account,
                        ...updatedBankAccount
                    }
                }
                toast.success('Proceso de cuenta bancaria actualizado exitosamente')
            })
            .addCase(updateProcessTrackingBankAccount.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
                toast.error('Error al actualizar el proceso de cuenta bancaria')
            })
    }
})

export const { clearProcessTracking } = processTrackingSlice.actions
export default processTrackingSlice.reducer